import React, { ReactNode } from 'react';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { phoneMediaQuery, rescheduleTheme } from '../rescheduleTheme';
import { RescheduleTypography } from './RescheduleTypography';

type WizardFrameProps = {
  logo: ReactNode;
  title: string;
  compactHeader?: boolean;
  headerAction?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
};

export const WizardFrame = ({ logo, title, compactHeader, headerAction, children, actions }: WizardFrameProps) => {
  const { i18n } = useTranslation();
  const isPhone = useMediaQuery(phoneMediaQuery);

  const logoWidth = compactHeader ? 56 : isPhone ? 84 : 150;
  const logoHeight = compactHeader ? 37 : isPhone ? 55 : 98;
  const frameGap = isPhone ? (compactHeader ? '16px' : '18px') : '28px';

  return (
    <Grid
      container
      dir={i18n.dir()}
      flexDirection="column"
      flexWrap="nowrap"
      alignItems="center"
      sx={{
        position: 'relative',
        minHeight: isPhone ? 'calc(100dvh - 100px)' : undefined,
        gap: frameGap,
        padding: isPhone ? (compactHeader ? '16px 14px 14px' : '18px 16px 16px') : '40px 48px 48px',
        border: `2px solid ${rescheduleTheme.brand}`,
        borderRadius: `${isPhone ? rescheduleTheme.radii.frameMobile : rescheduleTheme.radii.frame}px`,
        boxShadow: isPhone ? undefined : '0 2px 10px rgba(19,86,53,0.10)',
        background: '#FFFFFF',
        color: rescheduleTheme.text,
        fontFamily: rescheduleTheme.font,
      }}>
      <Grid
        component="header"
        container
        flexDirection={compactHeader ? 'row' : 'column'}
        flexWrap="nowrap"
        alignItems="center"
        sx={{
          gap: compactHeader ? '12px' : isPhone ? '4px' : '8px',
          alignSelf: compactHeader ? 'stretch' : undefined,
        }}>
        <Box
          sx={{
            width: logoWidth,
            height: logoHeight,
            flex: 'none',
            '& img': { width: '100%', height: '100%', objectFit: 'contain' },
          }}>
          {logo}
        </Box>
        {compactHeader ? (
          <RescheduleTypography component="span" style={{ fontSize: 17, fontWeight: 500 }}>
            {title}
          </RescheduleTypography>
        ) : (
          <RescheduleTypography
            variant="h1"
            style={{
              fontSize: isPhone ? 20 : 30,
              fontWeight: 500,
              letterSpacing: isPhone ? 'normal' : '-0.01em',
              textAlign: 'center',
            }}>
            {title}
          </RescheduleTypography>
        )}
        {headerAction && (
          <Grid
            item
            sx={{
              position: compactHeader ? 'static' : 'absolute',
              top: isPhone ? '10px' : '16px',
              insetInlineEnd: isPhone ? '10px' : '16px',
              marginInlineStart: compactHeader ? 'auto' : undefined,
            }}>
            {headerAction}
          </Grid>
        )}
      </Grid>
      <Grid
        container
        flexDirection="column"
        flexWrap="nowrap"
        sx={{
          maxWidth: 600,
          flex: '1 1 auto',
          gap: isPhone ? '16px' : '24px',
        }}>
        {children}
        {actions && (
          <Box
            sx={{
              marginTop: 'auto',
              display: 'flex',
              justifyContent: 'space-between',
              gap: isPhone ? '10px' : '12px',
              '& > *': isPhone ? { flex: 1 } : undefined,
            }}>
            {actions}
          </Box>
        )}
      </Grid>
    </Grid>
  );
};
